import { ascending } from "../../Utils/ArrayUtils";
import { dateTruncatingTime } from "../../Utils/DateUtils";
import { PackageType } from "./PackageTypes";

export default interface Package {
  readonly identifier: string;
  readonly type: PackageType;
  readonly buyDate: Date;
  readonly initialTokenCount: number;
  readonly leftTokenCount: number;
  readonly maxWeeklyBookingsCount: number;
  readonly startDate: Date;
  readonly expirationDate: Date | null;
  readonly updateDate: Date;
}

export type PackageState = "active" | "inactive" | "expired";

export const packageStatusActive = "active";
export const packageStatusInactive = "inactive";
export const packageStatusExpired = "expired";

export const getPackageState = (pack: Package, currentDate: Date): PackageState => {
  switch (true) {
    case pack.expirationDate && dateTruncatingTime(pack.expirationDate) < dateTruncatingTime(currentDate):
      return packageStatusExpired;
    case pack.startDate.getTime() > currentDate.getTime():
      return packageStatusInactive;
    default:
      return packageStatusActive;
  }
}

/**
 * Orders packages the way the backend charges them: the one expiring first goes first, since that
 * is what `getUserPackageInDate` picks when spending a token. A package without an expiration date
 * never runs out, so it goes last. Ties are broken by the most recently started, to keep the order
 * stable regardless of how Firestore returned the documents.
 */
const chargedFirst = <T extends Package>(lhs: T, rhs: T): number => {
  const lhsExpiration = lhs.expirationDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const rhsExpiration = rhs.expirationDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
  return (lhsExpiration - rhsExpiration)
    || (rhs.startDate.getTime() - lhs.startDate.getTime());
}

/**
 * The package the customer is entitled to use on the given date, i.e. the one whose validity
 * window contains that date. When more than one qualifies, a package with tokens left wins over a
 * spent one — the backend does not make that distinction, but showing a zero because of a spent
 * package the customer is not using would be misleading. Among equals, the one the backend would
 * charge first wins, so that the counter and the booking agree on which package is being consumed.
 */
export const selectCurrentPackage = <T extends Package>(
  packages: T[],
  currentDate: Date
): T | null => {
  const valid = packages.filter(pack => getPackageState(pack, currentDate) === packageStatusActive);
  const withTokensLeft = valid.filter(pack => pack.leftTokenCount > 0);
  const candidates = withTokensLeft.length > 0 ? withTokensLeft : valid;
  return candidates.sort(chargedFirst)[0] ?? null;
}

/**
 * The next package the customer will be able to use, i.e. the one starting soonest among those
 * already bought but not started yet. Booking is allowed as soon as a package is bought, for the
 * days that package covers, so this is what the customer spends next when nothing is valid today.
 */
export const selectUpcomingPackage = <T extends Package>(
  packages: T[],
  currentDate: Date
): T | null => {
  return packages
    .filter(pack => getPackageState(pack, currentDate) === packageStatusInactive)
    .sort(ascending(pack => pack.startDate.getTime()))[0] ?? null;
}

/**
 * The packages worth showing to the customer on the given date: the ones still usable (active) or
 * already assigned for later (inactive). Expired packages are dropped as soon as one replaces
 * them, so that only a customer with nothing else left keeps seeing the last one.
 */
export const selectDisplayablePackages = <T extends Package>(
  packages: T[],
  currentDate: Date
): T[] => {
  const replacements = packages
    .filter(pack => getPackageState(pack, currentDate) !== packageStatusExpired);
  if (replacements.length > 0) {
    return replacements.sort(ascending(pack => pack.startDate.getTime()));
  }
  const mostRecent = [...packages]
    .sort((lhs, rhs) => rhs.startDate.getTime() - lhs.startDate.getTime())[0];
  return mostRecent ? [mostRecent] : [];
}
